import { useNavigate } from '@tanstack/react-router';
import type { DeckungAssetRef, DeckungOwner } from './version-deckung';

/**
 * The search params the "Tarif & Deckung" asset editor honours (routes `assetSearchSchema`,
 * `AssetEditorSearch`): which tab opens, a dialog to open on arrival, the variant to highlight.
 */
export interface DeckungEditorSearch {
  readonly tab?: 'variants' | 'claimConfig' | 'overview';
  readonly intent?: 'addVariant';
  readonly variant?: string;
}

/** "Tarifvariante anlegen" (ui-spec §10): the Tarif-Varianten tab with "Variante hinzufügen" open. */
export const ADD_VARIANT_SEARCH: DeckungEditorSearch = { tab: 'variants', intent: 'addVariant' };

/** A preview card (ui-spec §10): the version's Tarifübersicht with that variant highlighted. */
export const overviewSearch = (variant: string): DeckungEditorSearch => ({
  tab: 'overview',
  variant,
});

const ASSET_ROUTE: Readonly<Record<DeckungOwner, string>> = {
  tarif: '/insurance/tarifs/$slug/assets/$name',
  group: '/insurance/group-tarifs/$slug/assets/$name',
};

const GENERATION_ROUTE: Readonly<Record<DeckungOwner, string>> = {
  tarif: '/insurance/tarifs/$slug/generations/$generation',
  group: '/insurance/group-tarifs/$slug/generations/$generation',
};

/** Navigation into the asset editor and the generation page, by owner. */
export function useDeckungNavigation(): {
  readonly openAsset: (asset: DeckungAssetRef, search?: DeckungEditorSearch) => void;
  readonly openGeneration: (owner: DeckungOwner, slug: string, generation: string) => void;
} {
  const navigate = useNavigate();
  return {
    openAsset: (asset, search = {}) => {
      void navigate({
        to: ASSET_ROUTE[asset.owner],
        params: { slug: asset.slug, name: asset.name },
        search,
      });
    },
    openGeneration: (owner, slug, generation) => {
      void navigate({ to: GENERATION_ROUTE[owner], params: { slug, generation } });
    },
  };
}
